import React, { useState, useEffect } from 'react';
import {
  X, Calendar, User, Mail, CheckCircle, Clock,
  AlertCircle, FileText, Send, Reply, Heart
} from 'lucide-react';
import { format } from 'date-fns';

const RecommendationEmailHistory = ({ recommendation, onClose }) => {
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock email history data - in real implementation, this would come from the database
  useEffect(() => {
    const mockHistory = [
      {
        id: 1,
        type: 'initial_request',
        subject: 'Recommendation Letter Request',
        sent_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
        status: 'sent',
        content: `Dear ${recommendation.recommender_name},

I hope this email finds you well. I am writing to ask if you would be willing to write a letter of recommendation for my college applications.

I really enjoyed your ${recommendation.subject} class and felt that I grew significantly as a student under your guidance. I believe you have seen my academic growth and would be able to speak to my strengths as a learner.

I would be happy to provide you with my resume, personal statement, and any other materials that would be helpful. The deadline for most of my applications is ${recommendation.due_date ? format(new Date(recommendation.due_date), 'MMMM d, yyyy') : 'TBD'}.

Thank you for considering my request. I understand this is a significant time commitment and I truly appreciate your support.

Best regards,
${recommendation.student_name || 'Student Name'}`,
        teacher_response: recommendation.request_accepted ? 'accepted' : recommendation.request_declined ? 'declined' : 'pending'
      }
    ];

    // Add follow-up emails based on status
    if (recommendation.request_accepted) {
      mockHistory.push({
        id: 2,
        type: 'teacher_response',
        subject: 'Re: Recommendation Letter Request - Happy to Help!',
        sent_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
        status: 'received',
        content: `Hello!

I would be happy to write a recommendation letter for you. You were a wonderful student and I have many positive things to say about your work ethic and academic growth.

Please send me:
- Your resume
- Your personal statement or essay
- A list of the colleges you're applying to
- Any specific points you'd like me to highlight

I'll aim to complete the letters by ${recommendation.due_date ? format(new Date(recommendation.due_date), 'MMMM d') : 'your deadline'}.

Best of luck with your applications!

${recommendation.recommender_name}`,
        teacher_response: 'accepted'
      });

      if (recommendation.has_brag_sheet) {
        mockHistory.push({
          id: 3,
          type: 'materials_sent',
          subject: 'Recommendation Materials - Thank You!',
          sent_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
          status: 'sent',
          content: `Dear ${recommendation.recommender_name},

Thank you so much for agreeing to write my recommendation letter! I've attached the materials you requested:

- My current resume
- Personal statement draft
- List of colleges and programs
- Additional information about my goals and experiences in your class

Please let me know if you need anything else. I truly appreciate your support during this process.

Best regards,
${recommendation.student_name || 'Student Name'}`,
          attachments: ['Student_Resume.pdf', 'Personal_Statement.pdf', 'College_List.pdf', 'Brag_Sheet.pdf']
        });

        mockHistory.push({
          id: 4,
          type: 'completion_notice',
          subject: 'Recommendation Letters Completed',
          sent_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
          status: 'received',
          content: `Hello,

I wanted to let you know that I have completed and submitted your recommendation letters to all the colleges on your list.

You should receive confirmation emails from each school's admissions office within the next few days.

Wishing you all the best in your college applications!

${recommendation.recommender_name}`,
          teacher_response: 'completed'
        });
      }
    } else if (recommendation.request_declined) {
      mockHistory.push({
        id: 2,
        type: 'teacher_response',
        subject: 'Re: Recommendation Letter Request',
        sent_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
        status: 'received',
        content: `Hello,

Thank you for thinking of me for your recommendation letter. Unfortunately, I have already committed to writing several letters this application cycle and cannot take on additional requests.

I suggest reaching out to [other teacher suggestion] who also knows your work well and would be able to provide a strong recommendation.

Best of luck with your applications!

${recommendation.recommender_name}`,
        teacher_response: 'declined'
      });
    }

    setEmailHistory(mockHistory);
    setLoading(false);
  }, [recommendation]);

  const getEmailIcon = (type, status) => {
    switch (type) {
      case 'initial_request':
        return <Send className="w-4 h-4 text-azure-600" />;
      case 'teacher_response':
        return <Reply className="w-4 h-4 text-emerald-600" />;
      case 'materials_sent':
        return <FileText className="w-4 h-4 text-violet-600" />;
      case 'completion_notice':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      default:
        return <Mail className="w-4 h-4 text-slate-600" />;
    }
  };

  const getEmailTypeLabel = (type) => {
    switch (type) {
      case 'initial_request':
        return 'Initial Request';
      case 'teacher_response':
        return 'Teacher Response';
      case 'materials_sent':
        return 'Materials Sent';
      case 'completion_notice':
        return 'Completion Notice';
      case 'follow_up':
        return 'Follow-up';
      case 'thank_you':
        return 'Thank You';
      default:
        return 'Email';
    }
  };

  const getStatusBadge = (email) => {
    if (email.teacher_response === 'accepted') {
      return <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full">Accepted</span>;
    }
    if (email.teacher_response === 'declined') {
      return <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full">Declined</span>;
    }
    if (email.teacher_response === 'completed') {
      return <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">Completed</span>;
    }
    if (email.status === 'sent') {
      return <span className="bg-azure-100 text-azure-700 text-xs px-2 py-1 rounded-full">Sent</span>;
    }
    if (email.status === 'received') {
      return <span className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-full">Received</span>;
    }
    return <span className="bg-gold-100 text-gold-700 text-xs px-2 py-1 rounded-full">Pending</span>;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-500 mx-auto"></div>
          <p className="text-slate-600 mt-4">Loading email history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-5">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">Email History</h2>
              <p className="text-sm text-slate-600 mt-1">
                Communication with {recommendation.recommender_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Teacher Info Card */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold">
                {recommendation.recommender_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{recommendation.recommender_name}</h3>
                <p className="text-sm text-slate-600">{recommendation.subject} • {recommendation.recommender_email}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">Due: {recommendation.due_date ? format(new Date(recommendation.due_date), 'MMM d') : 'TBD'}</p>
                <p className="text-xs text-slate-600">{emailHistory.length} email{emailHistory.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {/* Email Timeline */}
          <div className="space-y-6">
            {emailHistory.map((email, index) => (
              <div key={email.id} className="relative">
                {/* Timeline Line */}
                {index < emailHistory.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-full bg-slate-200"></div>
                )}

                <div className="flex gap-3 sm:gap-4">
                  {/* Timeline Icon */}
                  <div className="flex-shrink-0 w-12 h-12 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center relative z-10">
                    {getEmailIcon(email.type, email.status)}
                  </div>

                  {/* Email Content */}
                  <div className="flex-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold text-slate-900">{getEmailTypeLabel(email.type)}</h4>
                          {getStatusBadge(email)}
                        </div>
                        <p className="text-sm font-medium text-slate-700 mb-1">{email.subject}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(email.sent_date), 'MMM d, yyyy • h:mm a')}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {email.status === 'sent' ? 'You' : recommendation.recommender_name}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Content */}
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {email.content}
                      </p>
                    </div>

                    {/* Attachments */}
                    {email.attachments && email.attachments.length > 0 && (
                      <div className="border-t border-slate-200 pt-3">
                        <p className="text-xs font-medium text-slate-600 mb-2">Attachments:</p>
                        <div className="flex flex-wrap gap-2">
                          {email.attachments.map((attachment, idx) => (
                            <span key={idx} className="bg-azure-100 text-azure-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {attachment}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {emailHistory.length === 0 && (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Email History Yet</h3>
              <p className="text-slate-600">
                Once you create and send a recommendation request, the email history will appear here.
              </p>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-6 mt-6 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              {emailHistory.length > 0 && (
                <>Last updated {format(new Date(emailHistory[emailHistory.length - 1].sent_date), 'MMM d, yyyy')}</>
              )}
            </div>
            <div className="flex items-center gap-3">
              {recommendation.request_accepted && recommendation.has_brag_sheet && (
                <button className="px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  Send Thank You
                </button>
              )}
              <button
                onClick={onClose}
                className="px-6 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationEmailHistory;